// The frozen task view (T32 follow-up, spec J5 step 4; moved out of task-page.tsx, review F-R8).
import { Link } from '@tanstack/react-router';
import { formatLocal } from '@cis/shared';
import { taskContentSchema, type FrozenStep, type FrozenTask } from '@cis/api-client';
import { useFrozenTaskView } from '../../api/evidence.ts';
import { ErrorPanel } from '../../ui/error-boundary.tsx';
import { Skeleton } from '../../ui/skeleton.tsx';
import { FrozenBadge } from '../evidence/labels.tsx';
import { StatusPill } from './status-pill.tsx';
import { FreezeBannerSlot } from './task-slots.tsx';
import '../../styles/ui.css';

const SKELETON_LINES = 10;

// FROZEN VIEW (T32 follow-up, spec J5 step 4): the task as the freeze stored it. Read-only: no
// actions, no tick boxes, no comments form; a "Frozen" badge on the title (Q26, Q32).

function FrozenStepItem(props: { readonly step: FrozenStep }): React.JSX.Element {
  const { step } = props;
  return (
    <li className="step">
      <p className="step__head">
        Step {step.no}
        {step.required ? '' : ' (optional)'}
      </p>
      <p className="step__text">{step.do}</p>
      <p className="muted">
        {step.tick === null
          ? 'Not ticked at the freeze.'
          : `Ticked by ${step.tick.by.displayName} on ${formatLocal(step.tick.at)}.`}
      </p>
    </li>
  );
}

function FrozenTaskBody(props: { readonly task: FrozenTask }): React.JSX.Element {
  const t = props.task;
  const content = taskContentSchema.parse(t.content);
  const people = t.assignees.map((p) => p.displayName).join(', ');
  return (
    <>
      <dl className="facts">
        <dt>Status</dt>
        <dd>
          <StatusPill status={t.state.status} />
        </dd>
        <dt>Phase</dt>
        <dd>{t.phase.code}</dd>
        <dt>Area</dt>
        <dd>{t.area}</dd>
        <dt>Assigned to</dt>
        <dd>{people === '' ? 'Nobody' : people}</dd>
        <dt>Done</dt>
        <dd>{t.state.percent}%</dd>
        {t.state.updateNotes !== null && t.state.updateNotes !== '' && (
          <>
            <dt>Update notes</dt>
            <dd>{t.state.updateNotes}</dd>
          </>
        )}
      </dl>
      {t.retired && (
        <p className="card card--alert" role="note">
          This task was retired at the freeze.
        </p>
      )}
      <section className="card" aria-labelledby="runbook-title">
        <h2 className="card__title" id="runbook-title">
          Runbook
        </h2>
        {content.description !== '' && <p className="step__text">{content.description}</p>}
        <ol className="steps" aria-label="Steps">
          {t.steps.map((step) => (
            <FrozenStepItem key={step.no} step={step} />
          ))}
        </ol>
      </section>
    </>
  );
}

export function FrozenTaskPage(props: {
  readonly taskId: string;
  readonly freezeId: number;
}): React.JSX.Element {
  const { taskId } = props;
  const view = useFrozenTaskView(props.freezeId, taskId);
  const task = view.data ?? null;
  return (
    <section className="page" aria-labelledby="task-title">
      <FreezeBannerSlot taskId={taskId} asOf={undefined} />
      <div className="page__head">
        <h1 className="page__title" id="task-title">
          <span className="mono">{taskId}</span> {task?.title ?? ''} <FrozenBadge />
        </h1>
        <Link to="/tasks" className="muted">
          Back to tasks
        </Link>
      </div>
      {view.isPending && <Skeleton lines={SKELETON_LINES} label="Loading the frozen record…" />}
      {view.isError && (
        <ErrorPanel
          title="The frozen record could not be shown"
          error={view.error}
          onRetry={() => void view.refetch()}
        />
      )}
      {task !== null && <FrozenTaskBody task={task} />}
    </section>
  );
}
